import type { Track } from "../api.js";
import { audioContext } from "./context.js";
import { TrackPlayer, type PlayerSnapshot } from "./player.js";
import { decomposeRate, type RateSetting } from "./rate.js";
import type { SegmentRequest } from "./load.js";
import {
  clampToWindow,
  contains,
  toBufferTime,
  toTrackTime,
  windowEnd,
  WHOLE_TRACK,
  type LoadedWindow,
} from "./window.js";
import { fetchBeats, fetchPeaks, type BeatData, type PeakData } from "../waveform/peaks.js";

/** Tempo fader travel, as a fraction. */
export const TEMPO_RANGES = [0.08, 0.16] as const;

/** How hard a nudge pushes, as a fraction of the current rate. */
const NUDGE_AMOUNT = 0.04;

export interface DeckSnapshot extends PlayerSnapshot {
  id: string;
  /** Tempo fader position, -1..1, scaled by the active range. */
  tempoFader: number;
  tempoRange: number;
  /** Detected or manual BPM of the loaded track, before the fader. */
  baseBpm: number | null;
  /** BPM as it is actually playing, after fader and half/double. */
  effectiveBpm: number | null;
  halfDouble: number;
  cue: number | null;
  nudging: boolean;
  peaksReady: boolean;
  /** Track-time region actually held in memory. */
  window: LoadedWindow;
  /** True when only part of the track is loaded. */
  excerpt: boolean;
}

/**
 * One deck: a player, plus the things that make it a DJ deck rather than a
 * media player - cue point, tempo fader, nudge, and the analysis data used to
 * draw it.
 */
export class Deck {
  readonly player: TrackPlayer;
  readonly output: GainNode;

  peaksOverview: PeakData | null = null;
  peaksDetail: PeakData | null = null;
  beats: BeatData | null = null;

  private tempoFader = 0;
  private tempoRangeIndex = 0;
  private halfDouble = 1;
  private cue: number | null = null;
  private nudgeDirection = 0;
  private analysisLoad: AbortController | null = null;
  /**
   * Everything outside the player speaks track time. The player speaks buffer
   * time. This is the only thing that knows the difference.
   */
  private loaded: LoadedWindow = WHOLE_TRACK;

  private readonly autoGain: GainNode;

  constructor(
    readonly id: string,
    private readonly onChange: (snapshot: DeckSnapshot) => void,
  ) {
    this.player = new TrackPlayer(() => this.emit());
    const ctx = audioContext();
    // Auto-gain is separate from output so the mixer's trim and crossfader can
    // move without disturbing the per-track loudness correction.
    this.autoGain = ctx.createGain();
    // Deck output is its own node so the Phase 5 mixer can insert EQ and the
    // crossfader between here and the destination without touching the player.
    this.output = ctx.createGain();
    this.player.output.connect(this.autoGain).connect(this.output);
  }

  snapshot(): DeckSnapshot {
    const base = this.player.snapshot();
    const baseBpm = base.track?.tempo ?? null;
    const rate = this.targetRate();
    return {
      ...base,
      id: this.id,
      tempoFader: this.tempoFader,
      tempoRange: TEMPO_RANGES[this.tempoRangeIndex] as number,
      baseBpm,
      effectiveBpm: baseBpm === null ? null : baseBpm * rate,
      halfDouble: this.halfDouble,
      // Position and duration are reported in track time, so the UI, the
      // waveforms and the planner all speak one unit.
      position: toTrackTime(this.loaded, base.position),
      duration: base.track?.duration ?? base.duration,
      cue: this.cue,
      nudging: this.nudgeDirection !== 0,
      peaksReady: this.peaksDetail !== null,
      window: this.loaded,
      excerpt: Number.isFinite(this.loaded.length),
    };
  }

  private emit(): void {
    this.onChange(this.snapshot());
  }

  /** Rate the deck should run at, ignoring any momentary nudge. */
  private targetRate(): number {
    const range = TEMPO_RANGES[this.tempoRangeIndex] as number;
    return (1 + this.tempoFader * range) * this.halfDouble;
  }

  private applyRate(): void {
    const nudge = 1 + this.nudgeDirection * NUDGE_AMOUNT;
    this.player.setRate(this.targetRate() * nudge);
  }

  async load(track: Track, segment?: SegmentRequest): Promise<void> {
    this.analysisLoad?.abort();
    this.peaksOverview = null;
    this.peaksDetail = null;
    this.beats = null;
    this.cue = null;

    // Apply the loudness correction (A6) before any audio starts, so tracks
    // arrive at the mixer at comparable levels and two decks summing together
    // do not clip the output.
    const ctx = audioContext();
    this.autoGain.gain.setTargetAtTime(track.gain ?? 1, ctx.currentTime, 0.01);

    const controller = new AbortController();
    this.analysisLoad = controller;

    // Peaks and beats are small and independent of the audio; fetching them
    // alongside the decode means the waveform appears as soon as it can.
    const analysis = Promise.allSettled([
      fetchPeaks(track.id, "overview", controller.signal),
      fetchPeaks(track.id, "detail", controller.signal),
      fetchBeats(track.id, controller.signal),
    ]).then(([overview, detail, beats]) => {
      if (controller.signal.aborted) return;
      this.peaksOverview = overview.status === "fulfilled" ? overview.value : null;
      this.peaksDetail = detail.status === "fulfilled" ? detail.value : null;
      this.beats = beats.status === "fulfilled" ? beats.value : null;
      this.emit();
    });

    await Promise.all([this.player.load(track, segment), analysis]);
    this.loaded = segment
      ? { offset: segment.start, length: this.player.snapshot().duration }
      : WHOLE_TRACK;
    this.applyRate();
  }

  unload(): void {
    this.loaded = WHOLE_TRACK;
    this.analysisLoad?.abort();
    this.peaksOverview = null;
    this.peaksDetail = null;
    this.beats = null;
    this.cue = null;
    this.player.unload();
  }

  /** Playback position in track time. */
  position(): number {
    return toTrackTime(this.loaded, this.player.position());
  }

  /** The region of the track held in memory. */
  window(): LoadedWindow {
    return this.loaded;
  }

  async togglePlay(): Promise<void> {
    await this.player.toggle();
  }

  /** Seek to a track-time position, clamped to what is actually loaded. */
  seek(seconds: number): void {
    this.player.seek(toBufferTime(this.loaded, clampToWindow(this.loaded, seconds)));
  }

  /** Whether a track-time position can be played without reloading. */
  holds(trackTime: number): boolean {
    return contains(this.loaded, trackTime);
  }

  /** End of the loaded region, in track time. */
  loadedEnd(): number {
    return windowEnd(this.loaded);
  }

  setCue(seconds?: number): void {
    this.cue = seconds ?? this.position();
    this.emit();
  }

  /** Jump to the cue point, or to the start if none is set. */
  returnToCue(): void {
    this.player.seek(this.cue ?? 0);
  }

  setTempoFader(value: number): void {
    this.tempoFader = Math.max(-1, Math.min(1, value));
    this.applyRate();
  }

  cycleTempoRange(): void {
    this.tempoRangeIndex = (this.tempoRangeIndex + 1) % TEMPO_RANGES.length;
    this.applyRate();
  }

  resetTempo(): void {
    this.tempoFader = 0;
    this.applyRate();
  }

  /** Toggle between half, normal and double time, for octave errors (C5). */
  setHalfDouble(factor: number): void {
    this.halfDouble = factor;
    this.applyRate();
  }

  /**
   * Momentary tempo bump. Held down to pull a drifting deck back into phase,
   * released to return to the fader's rate (P5).
   */
  startNudge(direction: 1 | -1): void {
    if (this.nudgeDirection === direction) return;
    this.nudgeDirection = direction;
    this.applyRate();
  }

  endNudge(): void {
    if (this.nudgeDirection === 0) return;
    this.nudgeDirection = 0;
    this.applyRate();
  }

  /**
   * Set the total playback multiplier, decomposing it into a half/double
   * setting, a fader range and a fader position. Used by auto-mix to apply a
   * matched rate through the same controls the operator uses.
   *
   * Widens the fader range when it has to. Previously this used whatever range
   * happened to be selected and silently clamped, so a -10% match requested
   * while the deck sat on the +/-8% range came out at -8%: a deck playing at a
   * confidently wrong tempo. The returned setting reports whether the request
   * was actually met, and callers must check it.
   */
  setPlaybackMultiplier(total: number): RateSetting {
    const setting = decomposeRate(total, TEMPO_RANGES);
    this.halfDouble = setting.octave;
    this.tempoRangeIndex = setting.rangeIndex;
    this.tempoFader = setting.fader;
    this.applyRate();
    return setting;
  }

  /** Start at a scheduled audio-clock time, from a track-time offset. */
  async playAt(when: number, trackOffset: number): Promise<void> {
    await this.player.playAt(when, toBufferTime(this.loaded, clampToWindow(this.loaded, trackOffset)));
  }

  /**
   * Bend the playback rate over a scheduled window (X13a).
   *
   * Bypasses the tempo fader deliberately: the whole point is to travel further
   * than the fader can. The fader is then reset to match the new resting rate,
   * so the displayed controls still describe what the deck is doing.
   */
  rampToMultiplier(target: number, startAt: number, duration: number): void {
    this.player.rampRate(target * (1 + this.nudgeDirection * NUDGE_AMOUNT), startAt, duration);
    const setting = decomposeRate(target, TEMPO_RANGES);
    this.halfDouble = setting.octave;
    this.tempoRangeIndex = setting.rangeIndex;
    this.tempoFader = setting.fader;
    this.emit();
  }

  /** Schedule one repeat of a loop roll, from a track-time offset. */
  scheduleSlice(when: number, trackOffset: number, duration: number): void {
    this.player.scheduleSlice(when, toBufferTime(this.loaded, trackOffset), duration);
  }

  /** Silence the continuous source at a scheduled time. */
  stopAt(when: number): void {
    this.player.stopAt(when);
  }

  /** Drag the waveform to shove playback forward or back (P6). */
  shove(seconds: number): void {
    this.player.seek(this.position() + seconds);
  }
}
